import {
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonCard,
  IonCardContent,
  IonButton,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonContent,
  IonLoading,
  IonModal,
  IonToast,
  IonSelect,
  IonSelectOption,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { decodeParam } from "../util/ApiHelper";
import { getAllFields, MeterReadings } from "../models/workorders/MeterReading";
import "./MeterReadings.css";
import OnlineStatus from "./OnlineStatus";
import { TranslationsContext } from "../util/Translations";
import { postMeterReadings, putMeterReadings } from "../api/MeterReadings";
import { TranslationMessagesContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetMeterMetadata from "../hooks/work-order/useGetMeterMetadata";
import { useOfflineStore } from "../store/offline-mode";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";

interface MeterReadingModalProps {
  isOpen: boolean;
  cancel: () => void;
  confirm: (meterReading: any) => void;
  readDate?: string;
  readTime?: string;
  reading?: number;
  comments?: string;
  equipSerial: string;
  isSerial: string;
}

const MeterReadingsModal: React.FC<MeterReadingModalProps> = (props) => {
  const {
    isOpen,
    cancel,
    confirm,
    readDate,
    readTime,
    reading = 0,
    comments = "",
    equipSerial,
    isSerial,
  } = props;
  const { translations } = useContext(TranslationsContext);
  const { workOrderId, meter } = useParams<{
    workOrderId: string;
    meter: string;
  }>();
  let displayMeter = decodeParam(meter);
  let reportedOnWoBase = decodeParam(workOrderId);

  let [historyReading, setHistoryReading] = useState<number>(reading ?? 0);
  let [textHistoryReading, setTextHistoryReading] = useState<string>();
  let [historyComments, setHistoryComments] = useState<string>(comments ?? "");
  const metadata = useWorkOrderStore((state) => state.newMeterMetaData);
  const isOffline = useOfflineStore((state) => state.isOffline);
  const addNewMeterReadings = useWoOfflineUpdatesStore(
    (state) => state.addNewMeterReadings
  );
  const { getMeterMetadata } = useGetMeterMetadata();

  const [showLoading, setShowLoading] = useState(false);
  let date = new Date();
  let convertDate: string;
  if (readDate) {
    convertDate = decodeURIComponent(readDate);
    date = new Date(convertDate);
  }
  const [showToast, setShowToast] = useState(false);
  let [errorMessage, setErrorMessage] = useState<string>();
  const [presentAlert] = useIonAlert();
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const meterReading = useWorkOrderStore(
    useShallow((state) =>
      state.getMeterReadingByMeter(displayMeter, reportedOnWoBase)
    )
  );
  const elementType = meterReading?.ElementType;
  const lastReading = meterReading?.LastReading ?? "";
  const meterUOM = meterReading?.MeterUom ?? "";
  const validValues = meterReading?.ReadingValidValues ?? "";
  const [presentToast] = useIonToast();

  const onCancel = () => {
    // Clear modal data on cancel
    if (readDate && readTime) {
      setHistoryReading(reading ?? 0);
      setHistoryComments(comments ?? "");
    } else {
      setHistoryReading(0);
      setHistoryComments("");
    }
    cancel();
  };

  const metadataQuery = useCallback(async () => {
    if (metadata) return; //got it from cache
    await getMeterMetadata(false, true);
  }, [getMeterMetadata, metadata]);

  async function saveMeterReading() {
    const data = {
      Reading: textHistoryReading ?? historyReading ?? "",
      PmScheduleMeterFlag: false,
      ElementType: "N",
      Comments: historyComments,
      ReportedOnWoBase: reportedOnWoBase ?? "",
      ReportedOnWoTask: "",
      ReportedOnWoSubTask: "",
      MeterUom: meterUOM,
      EquipSerialId: equipSerial,
      EquipSerialInd: isSerial,
      Meter: displayMeter,
      // US 922643: send machine formats so the reading saves the same for every
      // user and phone. The phone's locale date ("15.10.2026" in German, or US
      // month-first read day-first by the server) could swap day and month,
      // and the time was sent in UTC. Reading time is plant-local, like the
      // time EAM web stores.
      ReadingDate: moment(date).format("YYYY-MM-DD"),
      ReadingTime: !!readTime ? readTime : moment(date).format("HH:mm:ss.SSS"),
    };
    if (!textHistoryReading && elementType === "T") {
      presentToast({
        message: "Meter Reading is required.",
        duration: 1000,
        position: "top",
      });
      return;
    }

    let numlastReading =
      data.ElementType === "N" && typeof lastReading === "string"
        ? parseFloat(lastReading)
        : lastReading;
    const reads: MeterReadings[] = [];

    const isUpdate =
      readDate &&
      readTime &&
      data.ElementType === "N" &&
      numlastReading > data.Reading;

    if (isOffline) {
      addNewMeterReadings({
        data,
        isUpdate: !!isUpdate,
        woNumber: reportedOnWoBase,
      });
      confirm(meterReading);
      presentAlert({
        message:
          translatedMessages["MobOfflineMeterReading"]?.MessageText ||
          "Meter reading added in offline store.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      return; //will sync the data once user comes online
    }

    if (isUpdate) {
      putMeterReadings(data).then((response) => {
        reads.push(data);
        if (response.status !== 200) {
          setShowToast(true);
          setErrorMessage(response.data);
        } else {
          confirm(meterReading);
          setHistoryReading(0);
          setHistoryComments(comments ?? "");
        }
      });
    } else if (data.ElementType === "N" && numlastReading > data.Reading) {
      presentAlert({
        header:
          translatedMessages[
            "MeterReadingLessThanPrevious"
          ]?.MessageText.replace("@1", data.Reading).replace(
            "@2",
            numlastReading
          ) ||
          `The Reading entry of ${data.Reading} is less than the Previous Reading ${numlastReading}. Do you wish to continue?`,
        buttons: [
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Ok",
            handler: () => {
              postReadings(data, reads);
            },
          },
        ],
      });
    } else {
      postReadings(data, reads);
    }
  }

  async function postReadings(data: any, reads: MeterReadings[]) {
    if (isOffline) {
      addNewMeterReadings({
        data,
        isUpdate: false,
        woNumber: reportedOnWoBase,
      });
      confirm(meterReading);
      presentAlert({
        message:
          translatedMessages["MobOfflineMeterReading"]?.MessageText ||
          "Meter reading added in offline store.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      return; //will sync the data once user comes online
    }
    postMeterReadings(data).then((response) => {
      reads.push(data);
      if (response.status !== 200) {
        setShowToast(true);
        setErrorMessage(response.data);
      } else {
        confirm(meterReading);
        setHistoryReading(0);
        setHistoryComments("");
      }
    });
  }

  useEffect(() => {
    // getEquipSerial();
    //Reset data when switching meter readings.
    if (reading) {
      setHistoryReading(reading);
    } else {
      setHistoryReading(0);
    }
    if (comments) {
      setHistoryComments(comments);
    } else {
      setHistoryComments("");
    }

    // Show loading symbol
    setShowLoading(true);
    //Run api calls to get metadata and meter reading.
    metadataQuery().then(() => {
      // Hide loading symbol once all the calls are done
      setShowLoading(false);
    });
  }, [
    equipSerial,
    displayMeter,
    workOrderId,
    lastReading,
    meterUOM,
    reading,
    comments,
    metadataQuery,
  ]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onCancel}
      className="meter-readings"
    >
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onCancel}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          {!!readDate && !!readTime ? (
            <IonTitle>
              {translations["lbl_edit_reading"] || "Edit Meter Reading"}
            </IonTitle>
          ) : (
            <IonTitle>
              {translations["lbl_add_reading"] || "Add Meter Reading"}
            </IonTitle>
          )}
          <IonButtons slot="end">
            <IonButton expand="full" onClick={() => saveMeterReading()}>
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <OnlineStatus />
      </IonHeader>
      <IonContent fullscreen>
        {!!metadata && !!meterReading && (
          <>
            <IonToast
              isOpen={showToast}
              onDidDismiss={() => setShowToast(false)}
              message={errorMessage}
              duration={3000}
              position="top"
            />
            <IonCard>
              <IonCardContent>
                <IonList lines="none">
                  {getAllFields(metadata).map((meterField: any) => {
                    const validValueList = validValues
                      ?.split(",")
                      .filter((v: any) => v && v.trim().length > 0);
                    return (
                      <IonItem
                        key={meterField.IdText}
                        className="labeled-inputs"
                      >
                        <IonLabel
                          position="stacked"
                          color="dark"
                          className="read-only-input-label"
                        >
                          {meterField.TranslatedIdText}
                        </IonLabel>
                        {meterField.PropertyName === "EquipSerialId" ? (
                          <IonInput
                            readonly
                            class="read-only-input"
                            value={equipSerial}
                          ></IonInput>
                        ) : meterField.PropertyName === "Description" ? (
                          <IonInput readonly class="read-only-input"></IonInput>
                        ) : meterField.PropertyName === "Meter" ? (
                          <IonInput
                            readonly
                            class="read-only-input"
                            value={displayMeter}
                          ></IonInput>
                        ) : meterField.PropertyName === "LastReading" ? (
                          <IonInput
                            readonly
                            class="read-only-input"
                            value={lastReading}
                          ></IonInput>
                        ) : meterField.PropertyName === "Reading" &&
                          elementType === "T" ? (
                          validValueList && validValueList.length > 0 ? (
                            <IonSelect
                              interface="popover"
                              className="editable-select-input required-field"
                              value={textHistoryReading}
                              onIonChange={(ev) =>
                                setTextHistoryReading(ev.detail.value)
                              }
                            >
                              {validValueList.map(
                                (list: any, index: number) => (
                                  <IonSelectOption key={index}>
                                    {list}
                                  </IonSelectOption>
                                )
                              )}
                            </IonSelect>
                          ) : (
                            <IonInput
                              className="editable-input required-field"
                              value={textHistoryReading}
                              required={true}
                              onIonChange={(ev) => {
                                if (
                                  ev.detail.value === null ||
                                  ev.detail.value === undefined
                                )
                                  return;
                                setTextHistoryReading(ev.detail.value);
                              }}
                            />
                          )
                        ) : meterField.PropertyName === "Reading" &&
                          elementType === "N" ? (
                          <IonInput
                            class="editable-input"
                            type="number"
                            inputMode="decimal"
                            value={historyReading}
                            onIonChange={(ev) => {
                              if (
                                ev.detail.value === null ||
                                ev.detail.value === undefined
                              )
                                return;
                              setHistoryReading(+ev.detail.value);
                            }}
                          ></IonInput>
                        ) : meterField.PropertyName === "MeterUom" ? (
                          <IonInput
                            readonly
                            class="read-only-input"
                            value={meterUOM}
                          ></IonInput>
                        ) : meterField.PropertyName === "Comments" ? (
                          <IonInput
                            class="editable-input"
                            value={historyComments}
                            onIonChange={(ev) => {
                              if (
                                ev.detail.value === null ||
                                ev.detail.value === undefined
                              )
                                return;
                              setHistoryComments(ev.detail.value);
                            }}
                          ></IonInput>
                        ) : (
                          <></>
                        )}
                      </IonItem>
                    );
                  })}
                </IonList>
              </IonCardContent>
            </IonCard>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default MeterReadingsModal;
